import { useEffect, useMemo, useRef, useState } from 'react';
import { CommandBar, PrivacyChip } from '@life-os/ui';
import { searchAll } from '../lib/search.js';
import { useStore } from './store.js';
import { useRouter } from './router.js';
import { useAsync } from '../lib/useAsync.js';
import { Icon, iconNameForRoute } from './icons.js';

const GROUP_LABEL: Record<string, string> = {
  system: 'Systems',
  task: 'Tasks',
  capture: 'Captured',
  note: 'Notes',
  source: 'Sources',
  review: 'Reviews',
};

const KIND_ICON: Record<string, string> = {
  task: 'tasks',
  capture: 'capture',
  note: 'notes',
  source: 'source',
  review: 'review',
};

function resultIcon(kind: string, surface: string): string {
  if (surface.startsWith('/')) return iconNameForRoute(surface);
  return KIND_ICON[kind] ?? 'dot';
}

export function CommandPalette({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { registry, data } = useStore();
  const { navigate } = useRouter();
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState(0);
  const listRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (open) {
      setQuery('');
      setSelected(0);
    }
  }, [open]);

  const { value: groups } = useAsync(() => searchAll(query, registry, data), [query, open]);

  // Flatten groups into a single navigable list (with its icon + group kind).
  const flat = useMemo(
    () =>
      (groups ?? []).flatMap((g) =>
        g.results.map((r) => ({ ...r, kind: g.kind, icon: resultIcon(g.kind, r.surface) })),
      ),
    [groups],
  );

  useEffect(() => {
    setSelected((s) => Math.min(s, Math.max(0, flat.length - 1)));
  }, [flat.length]);

  const go = (surface: string) => {
    if (surface.startsWith('/')) navigate(surface);
    onClose();
  };

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') return onClose();
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelected((s) => (flat.length ? (s + 1) % flat.length : 0));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelected((s) => (flat.length ? (s - 1 + flat.length) % flat.length : 0));
      } else if (e.key === 'Enter' && flat[selected]) {
        e.preventDefault();
        go(flat[selected].surface);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, flat, selected, onClose]);

  // Keep the selected row in view.
  useEffect(() => {
    const el = listRef.current?.querySelector('[data-selected="true"]');
    el?.scrollIntoView({ block: 'nearest' });
  }, [selected]);

  if (!open) return null;

  let idx = -1;
  return (
    <div className="palette__scrim" onClick={onClose} role="presentation">
      <div className="palette" onClick={(e) => e.stopPropagation()} role="dialog" aria-label="Command palette">
        <div className="palette__head">
          <CommandBar value={query} onChange={setQuery} autoFocus placeholder="Search systems, tasks, captures…" />
        </div>
        <div className="palette__results" ref={listRef}>
          {(groups ?? []).map((g) => (
            <div key={g.kind}>
              <div className="palette__group-label lc-label">{GROUP_LABEL[g.kind] ?? g.kind}</div>
              {g.results.map((r) => {
                idx += 1;
                const isSel = idx === selected;
                return (
                  <div
                    key={r.id}
                    className="lc-row palette__row"
                    data-interactive="true"
                    data-selected={isSel}
                    role="button"
                    tabIndex={0}
                    onClick={() => go(r.surface)}
                    onMouseMove={() => setSelected(idx)}
                  >
                    <span className="palette__row-icon">
                      <Icon name={resultIcon(g.kind, r.surface)} size={17} />
                    </span>
                    <div className="lc-row__main">
                      <div className="lc-row__title">{r.title}</div>
                      <div className="lc-row__sub">{r.preview}</div>
                    </div>
                    <div className="lc-row__meta">
                      <PrivacyChip value={r.privacyClass} />
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
          {flat.length === 0 ? (
            <div className="lc-empty">
              <div className="lc-empty__title">No matches</div>
              <div>Try a different term, or open All Systems to browse everything.</div>
            </div>
          ) : (
            <div className="palette__hint">
              <kbd>↑</kbd><kbd>↓</kbd> navigate · <kbd>↵</kbd> open · <kbd>esc</kbd> close
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
